import React, { useState, useMemo, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import {
  makeStyles, mergeClasses, tokens,
  Button, ToggleButton, TabList, Tab, Spinner,
} from '@fluentui/react-components';
import useFurnitureDrawings from '../configurator/summary/useFurnitureDrawings';
import ImageLightbox from '../components/shop/ImageLightbox';
import { Card } from '../theme/primitives';
import { iconMap } from '../theme/icons';
import { API_HOST } from '../config.js';

// Lazy: Interactive3DViewer pulls in Builder3D (the full r3f 3D editor),
// which is large. Loading it only on "Žiūrėti 3D" click keeps it out of
// every /baldai/:code visitor's initial bundle.
const Interactive3DViewer = lazy(() => import('./Interactive3DViewer'));

const PlayCircleIcon = iconMap.play_circle;
const FullscreenIcon = iconMap.fullscreen;
const ZoomInIcon = iconMap.zoom_in;
const ChevronLeftIcon = iconMap.chevron_left;
const ChevronRightIcon = iconMap.chevron_right;
const ViewInArIcon = iconMap.view_in_ar;

// A saved furniture record's `cabinets` are grouped by furnitureId elsewhere
// (groupCabinetsByFurniture) to split a ROOM layout into its separate,
// independently-priced pieces (used by Suvestinė and the live configurator's
// multi-piece editing). A single /baldai/:code shop item is always ONE
// product even when it's internally built from several cabinet modules side
// by side (e.g. a fitted wardrobe wall) — those modules share one furnitureId
// grouping key in the saved data purely for structural reasons, not because
// they're separate purchasable pieces. Splitting the drawing tabs by that
// internal grouping (previously via groupCabinetsByFurniture) produced a
// confusing "Baldas 1 / Baldas 2 / ..." picker and separate captures for what
// is, to the customer, one combined product — so this page always captures
// ALL cabinets together as a single unit, exactly like the configurator's own
// combined ("Tik baldas") 2D view already renders a multi-module piece as one
// continuous drawing.
const buildCombinedGroup = (cabinets = []) => {
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, depth = 0;
    cabinets.forEach((cab) => {
        const x = Number(cab.x) || 0; const y = Number(cab.y) || 0;
        const w = Number(cab.width) || 0; const h = Number(cab.height) || 0;
        minX = Math.min(minX, x); maxX = Math.max(maxX, x + w);
        minY = Math.min(minY, y); maxY = Math.max(maxY, y + h);
        depth = Math.max(depth, Number(cab.depth) || 0);
    });
    if (!isFinite(minX)) return null;
    return {
        furnitureId: 'combined', index: 0, label: 'Baldas',
        cabinets, x: minX, y: minY, width: maxX - minX, height: maxY - minY, depth,
    };
};

// PASTABA: anksčiau čia buvo ir trečias "Baldas" skirtukas, bet jis naudojo TĄ PATĮ
// 'exterior' raktą kaip "Išorė" — todėl rodydavo lygiai tą patį vaizdą (useFurnitureDrawings
// palaiko tik du fiksavimo tipus, exterior/interior, žr. jo targets sąrašą — trečio,
// atskiro "assembled" vaizdo tiesiog nėra). Pašalintas kaip klaidinantis dublikatas.
const STEP_TABS = [
    { key: 'exterior', labelKey: 'furniture_drawing_tabs.tab_exterior', icon: 'door_front' },
    { key: 'interior', labelKey: 'furniture_drawing_tabs.tab_interior', icon: 'shelves' },
];

// Griffel makeStyles — spalvos/tarpai/šešėliai per Fluent token'us; pačios galerijos
// struktūrinės reikšmės (minHeight:420 peržiūros langelis, miniatiūrų 64/80px matmenys)
// lieka literalios, nes tai konkretūs išdėstymo dydžiai be semantinio token atitikmens
// (ta pati logika, kaip ImageLightbox.jsx / FurnitureFilters.jsx).
const useStyles = makeStyles({
    root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
    galleryRow: { display: 'flex', flexDirection: 'column', gap: tokens.spacingHorizontalM, '@media (min-width: 640px)': { flexDirection: 'row' } },

    thumbBar: {
        order: 2, display: 'flex', gap: tokens.spacingHorizontalS, overflowX: 'auto', flexShrink: 0,
        '@media (min-width: 640px)': { order: 1, flexDirection: 'column', overflow: 'visible', width: '80px' },
    },
    // Realus Fluent <ToggleButton> — tik kompaktiškas kvadrato dydis ir
    // paveikslėlio/video vidinio turinio talpinimas be papildomo paddingo
    // (žr. #5).
    thumbBtn: {
        flexShrink: 0, minWidth: 'unset', width: '64px', height: '64px',
        overflow: 'hidden', padding: 0,
        '@media (min-width: 640px)': { width: '100%' },
    },
    thumbVideoWrap: { position: 'relative', width: '100%', height: '100%' },
    thumbMedia: { width: '100%', height: '100%', objectFit: 'contain', padding: tokens.spacingHorizontalXXS },
    thumbPlayIcon: { position: 'absolute', inset: 0, margin: 'auto', color: tokens.colorNeutralForegroundInverted, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))' },

    mainCard: {
        order: 1, overflow: 'hidden', flex: 1, minWidth: 0,
        '@media (min-width: 640px)': { order: 2 },
    },
    viewerFallback: {
        height: '420px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: tokens.spacingVerticalM, color: tokens.colorNeutralForeground3, backgroundColor: tokens.colorNeutralBackground1,
    },
    viewerFallbackLabel: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightMedium },

    videoSlide: { position: 'relative', backgroundColor: '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '420px' },
    videoEl: { width: '100%', maxHeight: '600px', minHeight: '420px' },

    // Realus Fluent <Button appearance="outline" shape="circular"> jau
    // suteikia kraštus/hover pagal Fluent2 standartą (žr. #5) — čia tik
    // plaukiojanti pozicija.
    navBtn: {
        position: 'absolute', top: '50%', transform: 'translateY(-50%)',
        boxShadow: tokens.shadow4, backgroundColor: tokens.colorNeutralBackground1,
    },
    navBtnLeft: { left: tokens.spacingHorizontalS, '@media (min-width: 640px)': { left: tokens.spacingHorizontalXXL } },
    navBtnRight: { right: tokens.spacingHorizontalS, '@media (min-width: 640px)': { right: tokens.spacingHorizontalXXL } },

    zoomOuter: { position: 'relative' },
    zoomBtn: {
        backgroundColor: tokens.colorNeutralBackground1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', width: '100%', position: 'relative', minHeight: '420px', border: 'none', padding: 0,
    },
    zoomImg: { width: '100%', height: '100%', objectFit: 'contain' },
    zoomOverlay: {
        position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0)',
        transitionProperty: 'background-color', transitionDuration: tokens.durationNormal,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    zoomOverlayHover: { backgroundColor: 'rgba(0,0,0,0.1)' },
    zoomIcon: {
        color: tokens.colorNeutralForegroundInverted, opacity: 0,
        transitionProperty: 'opacity', transitionDuration: tokens.durationNormal,
        filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))',
    },
    zoomIconHover: { opacity: 1 },
    // Realus Fluent <Button appearance="outline" shape="circular"> jau
    // suteikia kraštus/hover pagal Fluent2 standartą (žr. #5) — čia tik
    // plaukiojanti pozicija (opacity:0 — žr. pastabą žemiau).
    fullscreenBtn: {
        position: 'absolute', top: tokens.spacingVerticalS, right: tokens.spacingHorizontalS,
        boxShadow: tokens.shadow4, backgroundColor: tokens.colorNeutralBackground1,
        opacity: 0,
    },
    // PASTEBĖTA (išlaikyta kaip buvo): šis mygtukas originaliame Tailwind kode naudojo
    // `group-hover:opacity-100`, bet jo tėvinis elementas NETURĖJO `group` klasės
    // (`group` buvo ant KAIMYNINIO zoom mygtuko, ne ant bendro protėvio) — todėl jis
    // realiai NIEKADA netapdavo matomas hover metu jau prieš migraciją (liko
    // opacity:0, bet vis tiek paspaudžiamas). Elgsena perkelta tiksliai tokia pati,
    // nekeičiant funkcionalumo — jei norima, tai atskira, apie tai praneština pataisa.

    ratioFallback: {
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tokens.spacingVerticalM,
        color: tokens.colorNeutralForeground3,
    },
    ratioFallbackLabel: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightMedium },

    footerRow: {
        paddingLeft: tokens.spacingHorizontalM, paddingRight: tokens.spacingHorizontalM,
        paddingTop: tokens.spacingVerticalSNudge, paddingBottom: tokens.spacingVerticalSNudge,
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: tokens.spacingHorizontalM,
    },
    footerLeft: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalSNudge, minWidth: 0 },
    footerTabs: { minWidth: 0, flexShrink: 0 },
    footerStatus: {
        fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    },
});

// Rodo baldo Išorė/Vidus brėžinius (3D + techninis) — ta pati fiksavimo
// logika (useFurnitureDrawings), kaip Suvestinėje, tik pritaikyta paprastam,
// ne-MUI /baldai/:code puslapio stiliui.
const FurnitureDrawingTabs = ({ furniture }) => {
    const s = useStyles();
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(0);
    const [activeSlide, setActiveSlide] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    // "Padidinimas" (zoom) — kaip IKEA produkto puslapyje: paspaudus vaizdą jis
    // padidinamas VIETOJE (ne atskirame lange), o slankant pele per jį —
    // padidintas vaizdas seka pelės poziciją (CSS background-position, ne <img>).
    // Perjungiama atgal antru paspaudimu arba pasirinkus kitą skaidrę.
    const [zoomed, setZoomed] = useState(false);
    const [zoomPos, setZoomPos] = useState('50% 50%');
    // Dekoratyvus "užtemdymas + zoom_in piktograma" ties pagrindiniu vaizdu hover metu —
    // originale tai buvo CSS `group`/`group-hover` (tėvas-vaikas hover), Griffel atomic
    // klasėms tas pats pasiekiamas per lokalią hover būseną (tas pats vaizdinis efektas,
    // 100% deterministiška, be papildomos CSS selektorių rizikos).
    const [imgHover, setImgHover] = useState(false);
    const zoomBoxRef = useRef(null);
    // "Žiūrėti 3D" — kaip IKEA produkto puslapyje: perjungia pagrindinį vaizdą
    // iš statinių nufotografuotų skaidrių į TIKRĄ, laisvai sukamą 3D vaizdą
    // (žr. Interactive3DViewer.jsx).
    const [live3D, setLive3D] = useState(false);
    // Kai kurių senesnių baldų `lifestyleImagePath` rodo į failą, kurio serveryje
    // faktiškai nebėra (404) — anksčiau tokia skaidrė vis tiek likdavo sąraše ir
    // rodydavo naršyklės standartinę "sulaužyto vaizdo" piktogramą su alt tekstu.
    // Sekame, kurie src realiai nepasikrovė, ir tokias skaidres visai paslepiame
    // (20 punktų sąrašo #11), o ne rodome tuščią vietrikį.
    const [brokenSrcs, setBrokenSrcs] = useState(() => new Set());
    const markBroken = useCallback((src) => {
        setBrokenSrcs(prev => (prev.has(src) ? prev : new Set(prev).add(src)));
    }, []);

    const settings = useMemo(() => ({
        ...(furniture.settings || {}),
        cabinets: furniture.cabinets || [],
        sectionOptions: furniture.settings?.sectionOptions || {},
    }), [furniture]);

    const combinedGroup = useMemo(() => buildCombinedGroup(furniture.cabinets), [furniture.cabinets]);
    const groups = useMemo(() => combinedGroup ? [combinedGroup] : [], [combinedGroup]);

    const currentGroup = groups[0];
    const tab = STEP_TABS[activeTab];
    const key = currentGroup ? `${currentGroup.furnitureId}-${tab.key}` : null;

    // Fiksavimas anksčiau paleisdavo VISUS taikinius (kiekvieno baldo vieneto Išorė
    // IR Vidus) iš karto, vos puslapiui užsikrovus — bet vartotojas iš karto mato
    // TIK numatytąjį skirtuką ("Baldas"/Išorė). Kiekvienas nepamatytas taikinys vis
    // tiek atidarydavo savo WebGL kontekstą, o tai buvo pagrindinė priežastis, kodėl
    // mobiliuose telefonuose atsirasdavo "Context Lost" (žr. capturePhase/neededKeys
    // pastabas useFurnitureDrawings.jsx). Dabar fiksuojami TIK realiai peržiūrėti (ar
    // ką tik peržiūrimi) skirtukai — sąrašas auga tik vartotojui paspaudus kitą
    // skirtuką/baldo vienetą.
    const [viewedKeys, setViewedKeys] = useState([]);
    useEffect(() => {
        if (!key) return;
        setViewedKeys(prev => prev.includes(key) ? prev : [...prev, key]);
    }, [key]);

    // Perjungus skaidrę (kitą nuotrauką/vaizdą), padidinimas visada atstatomas —
    // priešingu atveju liktų "padidinta" pozicija iš VISAI kito vaizdo.
    useEffect(() => { setZoomed(false); }, [activeSlide, activeTab]);

    const handleZoomMove = useCallback((e) => {
        if (!zoomBoxRef.current) return;
        const rect = zoomBoxRef.current.getBoundingClientRect();
        const point = e.touches ? e.touches[0] : e;
        const x = Math.min(100, Math.max(0, ((point.clientX - rect.left) / rect.width) * 100));
        const y = Math.min(100, Math.max(0, ((point.clientY - rect.top) / rect.height) * 100));
        setZoomPos(`${x}% ${y}%`);
    }, []);

    const handleZoomToggle = useCallback((e) => {
        if (!zoomed) handleZoomMove(e);
        setZoomed(z => !z);
    }, [zoomed, handleZoomMove]);

    // Šis puslapis (ne Suvestinės PDF) rodo tik nedidelį, minHeight:420 peržiūros
    // langelį — Suvestinės numatytieji 1500×1050/1700×1050 fiksavimo matmenys čia
    // gerokai per dideli tam, kas realiai matoma, o fiksavimas paleidžiamas
    // AUTOMATIŠKAI vos puslapiui užsikrovus. Keliuose mobiliuose telefonuose
    // (ypač Safari/WebKit) toks atminties poreikis nuosekliai atidarant kelis
    // didelius WebGL kontekstus sukeldavo puslapio avariją per pirmas kelias
    // sekundes. Gerokai mažesni matmenys čia iš esmės sumažina tą apkrovą,
    // nekeičiant Suvestinės (kur kokybė svarbi spausdinamam dokumentui).
    const CAPTURE_SIZE = { width3d: 760, height3d: 532, width2d: 820, height2d: 500 };
    const { images, hiddenNode } = useFurnitureDrawings(groups.length > 0, groups, settings, CAPTURE_SIZE, viewedKeys);

    if (!currentGroup) return null;

    const shot = images[key] || {};

    // Vienas slider'is trims vaizdais: realistiška nuotrauka (jei sugeneruota) +
    // pasirinkto skirtuko (Išorė/Vidus) 3D vaizdas + techninis brėžinys. `kind`
    // naudojamas nuspręsti, ar Išorė/Vidus skirtukai šioje skaidrėje apskritai
    // prasmingi (nuotrauka nesikeičia priklausomai nuo skirtuko, tad ten jie
    // slepiami) IR ar lightbox'e reikia baltos "lapo" foninės dėžutės (brėžiniams
    // taip, realiam kambario foto — ne, ji jau pati užpildo visą kadrą).
    const KIND_LABELS = { '3d': t('furniture_drawing_tabs.kind_3d'), '2d': t('furniture_drawing_tabs.kind_2d') };
    const tabLabel = t(tab.labelKey);
    const slides = [
        // Video visada PIRMAS (jei baldui įkeltas) — likusios nuotraukos/brėžiniai po jo,
        // ta pati tvarka kaip anksčiau.
        furniture.videoPath && { src: `${API_HOST}${furniture.videoPath}`, label: t('furniture_drawing_tabs.video'), ready: true, kind: 'video' },
        furniture.lifestyleImagePath && { src: `${API_HOST}${furniture.lifestyleImagePath}`, label: t('furniture_drawing_tabs.photo'), ready: true, kind: 'lifestyle' },
        { src: shot.img3d, label: `${tabLabel} · ${KIND_LABELS['3d']}`, ready: !!shot.img3d, kind: '3d' },
        { src: shot.img2d, label: `${tabLabel} · ${KIND_LABELS['2d']}`, ready: !!shot.img2d, kind: '2d' },
    ].filter(Boolean).filter(sl => !sl.ready || !brokenSrcs.has(sl.src));

    const safeSlide = Math.min(activeSlide, slides.length - 1);
    const slide = slides[safeSlide];
    const showStepTabs = slide.kind !== 'lifestyle' && slide.kind !== 'video';
    const goPrev = () => setActiveSlide(i => (i - 1 + slides.length) % slides.length);
    const goNext = () => setActiveSlide(i => (i + 1) % slides.length);

    return (
        <div className={s.root}>
            {/* Galerija — vertikali miniatiūrų juosta kairėje (kaip IKEA produkto
                puslapyje) + didelis pagrindinis vaizdas; mobiliame ekrane miniatiūros
                virsta horizontalia, slenkama juosta po pagrindiniu vaizdu. */}
            <div className={s.galleryRow}>
                {slides.length > 1 && (
                    <div className={s.thumbBar}>
                        {slides.map((sl, i) => (
                            <ToggleButton
                                key={sl.label}
                                appearance={!live3D && i === safeSlide ? 'outline' : 'subtle'}
                                checked={!live3D && i === safeSlide}
                                onClick={() => { setActiveSlide(i); setLive3D(false); }}
                                aria-label={sl.label}
                                className={s.thumbBtn}
                            >
                                {sl.ready ? (
                                    sl.kind === 'video' ? (
                                        <div className={s.thumbVideoWrap}>
                                            <video src={sl.src} muted playsInline preload="metadata" className={s.thumbMedia} />
                                            <PlayCircleIcon className={s.thumbPlayIcon} fontSize={20} />
                                        </div>
                                    ) : (
                                        <img src={sl.src} alt={sl.label} className={s.thumbMedia} onError={() => markBroken(sl.src)} />
                                    )
                                ) : (
                                    <Spinner size="extra-tiny" />
                                )}
                            </ToggleButton>
                        ))}
                    </div>
                )}

                <Card className={s.mainCard} padding={0}>
                    {live3D ? (
                        <Suspense fallback={(
                            <div className={s.viewerFallback}>
                                <Spinner size="medium" />
                                <span className={s.viewerFallbackLabel}>{t('furniture_drawing_tabs.loading_3d')}</span>
                            </div>
                        )}>
                            <Interactive3DViewer furniture={furniture} height={420} />
                        </Suspense>
                    ) : slide.kind === 'video' ? (
                        <div className={s.videoSlide}>
                            {/* Video netaikomas zoom/lightbox — <video controls> jau turi savo
                                pilno ekrano mygtuką naršyklės valdikliuose. */}
                            <video key={slide.src} src={slide.src} controls playsInline className={s.videoEl} />

                            {slides.length > 1 && (
                                <>
                                    <Button appearance="outline" shape="circular" onClick={goPrev} aria-label={t('furniture_drawing_tabs.prev')} icon={<ChevronLeftIcon fontSize={16} />} className={mergeClasses(s.navBtn, s.navBtnLeft)} />
                                    <Button appearance="outline" shape="circular" onClick={goNext} aria-label={t('furniture_drawing_tabs.next')} icon={<ChevronRightIcon fontSize={16} />} className={mergeClasses(s.navBtn, s.navBtnRight)} />
                                </>
                            )}
                        </div>
                    ) : (
                        <div className={s.zoomOuter}>
                            <button
                                ref={zoomBoxRef}
                                type="button"
                                onClick={slide.ready ? handleZoomToggle : undefined}
                                onMouseEnter={() => setImgHover(true)}
                                onMouseMove={zoomed ? handleZoomMove : undefined}
                                onMouseLeave={() => { setZoomed(false); setImgHover(false); }}
                                disabled={!slide.ready}
                                className={s.zoomBtn}
                                style={{
                                    cursor: slide.ready ? (zoomed ? 'zoom-out' : 'zoom-in') : 'default',
                                    ...(zoomed ? {
                                        backgroundImage: `url(${slide.src})`,
                                        backgroundPosition: zoomPos,
                                        backgroundSize: '200%',
                                        backgroundRepeat: 'no-repeat',
                                    } : {}),
                                }}
                            >
                                {slide.ready ? (
                                    !zoomed && (
                                        <>
                                            <img src={slide.src} alt={slide.label} className={s.zoomImg} onError={() => markBroken(slide.src)} />
                                            <div className={mergeClasses(s.zoomOverlay, imgHover && s.zoomOverlayHover)}>
                                                <ZoomInIcon className={mergeClasses(s.zoomIcon, imgHover && s.zoomIconHover)} fontSize={28} />
                                            </div>
                                        </>
                                    )
                                ) : (
                                    <div className={s.ratioFallback}>
                                        <Spinner size="medium" />
                                        <span className={s.ratioFallbackLabel}>{t('furniture_drawing_tabs.preparing_drawing')}</span>
                                    </div>
                                )}
                            </button>

                            {slide.ready && !zoomed && (
                                <Button
                                    appearance="outline"
                                    shape="circular"
                                    onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }}
                                    aria-label={t('furniture_drawing_tabs.view_fullscreen')}
                                    icon={<FullscreenIcon fontSize={16} />}
                                    className={s.fullscreenBtn}
                                />
                            )}

                            {slides.length > 1 && !zoomed && (
                                <>
                                    <Button appearance="outline" shape="circular" onClick={goPrev} aria-label={t('furniture_drawing_tabs.prev')} icon={<ChevronLeftIcon fontSize={16} />} className={mergeClasses(s.navBtn, s.navBtnLeft)} />
                                    <Button appearance="outline" shape="circular" onClick={goNext} aria-label={t('furniture_drawing_tabs.next')} icon={<ChevronRightIcon fontSize={16} />} className={mergeClasses(s.navBtn, s.navBtnRight)} />
                                </>
                            )}
                        </div>
                    )}

                    {/* Vieno eilutė vietoj anksčiau buvusio atskiro Išorė/Vidus mygtukų
                        blokelio virš galerijos IR atskiro teksto po ja — dabar abu
                        susijungę į vieną: perjungiklis kairėje, "Žiūrėti 3D" (kai
                        prasminga — tik 3D skaidrėje) dešinėje. Visa eilutė (įsk. vien
                        tik status tekstą) rodoma TIK kai joje realiai yra bent vienas
                        mygtukas — kitaip (pvz. lifestyle nuotraukos skaidrėje) likdavo
                        vienišas "Nuotrauka" užrašas be jokios funkcijos (žr. /admin/bugs
                        "Istrink si bloka, jis reikalingas tik ten kur yra mygtukai"). */}
                    {(showStepTabs || live3D || slide.kind === '3d') && (
                    <div className={s.footerRow}>
                        <div className={s.footerLeft}>
                            {showStepTabs && (
                                <TabList
                                    className={s.footerTabs}
                                    size="small"
                                    selectedValue={activeTab}
                                    onTabSelect={(_e, data) => setActiveTab(data.value)}
                                >
                                    {STEP_TABS.map((stepTab, i) => (
                                        <Tab key={stepTab.key} value={i}>{t(stepTab.labelKey)}</Tab>
                                    ))}
                                </TabList>
                            )}
                            {!live3D && (
                                <span className={s.footerStatus}>
                                    {slide.kind === 'video' ? t('furniture_drawing_tabs.video') : slide.kind === 'lifestyle' ? t('furniture_drawing_tabs.photo') : KIND_LABELS[slide.kind]}
                                </span>
                            )}
                        </div>
                        {(live3D || slide.kind === '3d') && (
                            <ToggleButton
                                appearance={live3D ? 'primary' : 'outline'}
                                checked={live3D}
                                shape="circular"
                                size="small"
                                icon={<ViewInArIcon fontSize={15} />}
                                onClick={() => setLive3D(v => !v)}
                            >
                                {live3D ? t('furniture_drawing_tabs.close_3d') : t('furniture_drawing_tabs.view_3d')}
                            </ToggleButton>
                        )}
                    </div>
                    )}
                </Card>
            </div>

            {lightboxOpen && (
                <ImageLightbox
                    items={slides.filter(sl => sl.ready && sl.kind !== 'video').map(sl => ({ src: sl.src, alt: sl.label, whiteBg: sl.kind !== 'lifestyle' }))}
                    index={slides.filter(sl => sl.ready && sl.kind !== 'video').findIndex(sl => sl.label === slide.label)}
                    onNavigate={(i) => {
                        const readySlides = slides.filter(sl => sl.ready && sl.kind !== 'video');
                        const target = readySlides[i];
                        const fullIndex = slides.findIndex(sl => sl.label === target.label);
                        setActiveSlide(fullIndex);
                    }}
                    onClose={() => setLightboxOpen(false)}
                />
            )}

            {hiddenNode}
        </div>
    );
};

export default FurnitureDrawingTabs;
